import { activeSiteConfig } from "@config/site/site-config";
import { VALIDATION_RULES } from "@lib/constants/formValidations";
import { dateRegex } from "@lib/validators/regex/general";
import { getDateOfBirthMarketConfig, getDateRegex } from "@lib/utils/dateOfBirth";
interface ParsedDate {
    day: number | null;
    month: number | null;
    year: number | null;
}

const NULL_DATE = { day: null, month: null, year: null };
const DEFAULT_YEAR: number = 2000;

export const isValidAge = (dateString: string, country?: string): boolean => {
    if (!dateString || !dateString.includes("/")) return true;

    const parts = dateString.split("/");
    const usesNoYearFormat = !!getDateOfBirthMarketConfig(country);

    if (usesNoYearFormat && parts.length === 2) return true;

    const currentYear = new Date().getFullYear();
    const year = Number(parts[parts.length - 1]);

    return year >= currentYear - VALIDATION_RULES.dateOfBirth.maxAge && year <= currentYear;
};

const mapDefaultParts = (parts: string[]): ParsedDate => {
    if (parts.length !== 3) return NULL_DATE;
    const [dayStr, monthStr, yearStr] = parts;
    return {
        day: parseInt(dayStr, 10),
        month: parseInt(monthStr, 10),
        year: parseInt(yearStr, 10),
    };
};
const mapMonthDayParts = (parts: string[]): ParsedDate => {
    if (parts.length < 2) return NULL_DATE;
    const [monthStr, dayStr] = parts;
    return {
        month: parseInt(monthStr, 10),
        day: parseInt(dayStr, 10),
        year: DEFAULT_YEAR,
    };
};
const mapDayMonthParts = (parts: string[]): ParsedDate => {
    if (parts.length < 2) return NULL_DATE;
    const [dayStr, monthStr] = parts;
    return {
        day: parseInt(dayStr, 10),
        month: parseInt(monthStr, 10),
        year: DEFAULT_YEAR,
    };
};
/**
 * Parses a date string into day, month, and year components.
 * Assumes input is already validated by form validation schema.
 * Supports the full DD/MM/YYYY format, and the no-year MM/DD or DD/MM formats
 * configured per market in `@lib/utils/dateOfBirth`.
 */
export const parseDateComponents = (date: string): ParsedDate => {
    if (!date) {
        return NULL_DATE;
    }

    // Replace whitespace
    const trimmedDate = date
        .replace(/\s+/g, "")
        .split("/")
        // Ensure parts have leading zeros if single digit
        .map((part) => {
            if (part.length === 1) {
                return part.padStart(2, "0");
            }
            return part;
        })
        .join("/");

    const parts = trimmedDate.split("/");
    const country = activeSiteConfig.country.toUpperCase();
    const marketConfig = getDateOfBirthMarketConfig(country);
    const regex = marketConfig ? getDateRegex(country) : dateRegex;
    const match = trimmedDate.match(regex);

    if (!match) {
        return NULL_DATE;
    }

    if (!marketConfig) return mapDefaultParts(parts);
    return marketConfig.format === "MM/DD" ? mapMonthDayParts(parts) : mapDayMonthParts(parts);
};

export const formatDateToYYYYMMDD = (date: string) => {
    const dateObject = parseDateComponents(date);
    // Invalid date if any component is null
    if (Object.values(dateObject).includes(null)) {
        return "";
    }
    const day = String(dateObject.day).padStart(2, "0");
    const month = String(dateObject.month).padStart(2, "0");
    const year = String(dateObject.year);

    if ([day, month, year].some((value) => value === null)) {
        return "";
    }

    return `${year}-${month}-${day}`;
};

export const createDateString = (day: number, month: number, year: number): string | null => {
    // Falsy day or month means incomplete date
    if (!day || !month) return null;
    const dayStr = String(day).padStart(2, "0");
    const monthStr = String(month).padStart(2, "0");
    const hasNoYear = !!getDateOfBirthMarketConfig(activeSiteConfig.country.toUpperCase());
    if (hasNoYear) return `${DEFAULT_YEAR}-${monthStr}-${dayStr}`;
    return year ? `${year}-${monthStr}-${dayStr}` : null;
};
